import * as React from "react";
import { AnimatedExperienceTypeIcon } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/** The five animated types, with a tone color for the icon + glyphs. */
const TYPES = [
  { type: "PODCAST", label: "Podcast", color: "text-orange-500" },
  { type: "AI_VIDEO", label: "AI Video", color: "text-cyan-500" },
  { type: "QUEST", label: "Quest", color: "text-violet-500" },
  { type: "AI_EXPERT", label: "Expert", color: "text-emerald-500" },
  { type: "AI_NOTE", label: "Note", color: "text-sky-500" },
] as const;

/**
 * Preview cell. The stage is sized generously (and unbordered) so the
 * particles — which drift well beyond the icon by design — float freely
 * instead of spilling across a tight box edge. `md` needs more headroom than
 * `sm` because its notes/flag travel further up.
 */
function Tile({
  type,
  label,
  color,
  size = "md",
  forceActive = false,
  fallback,
}: {
  type: string;
  label: string;
  color: string;
  size?: "md" | "sm";
  forceActive?: boolean;
  fallback?: React.ReactNode;
}) {
  const [hover, setHover] = React.useState(false);
  const active = forceActive || hover;
  const interactive = !forceActive;
  const stage = size === "sm" ? "h-16 w-16" : "h-32 w-24";

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        tabIndex={interactive ? 0 : undefined}
        onMouseEnter={interactive ? () => setHover(true) : undefined}
        onMouseLeave={interactive ? () => setHover(false) : undefined}
        onFocus={interactive ? () => setHover(true) : undefined}
        onBlur={interactive ? () => setHover(false) : undefined}
        className={[
          "flex items-center justify-center rounded-xl outline-none transition-colors",
          stage,
          interactive
            ? "hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring/40"
            : "",
        ].join(" ")}
      >
        <AnimatedExperienceTypeIcon
          type={type}
          active={active}
          size={size}
          className={color}
          fallback={fallback}
        />
      </div>
      <span className="max-w-[6rem] text-center text-xs font-medium text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

export function AnimatedExperienceTypeIconDemo() {
  return (
    <Section
      id="animated-experience-type-icon"
      title="AnimatedExperienceTypeIcon"
      description='"Live icon" hover treatment for the five experience types. Each plays a distinct motion (bob / pan / wobble / heartbeat / sway) layered around its anchored lucide glyph — the icon never swaps out. Layers mount only while active (zero idle cost), reduced-motion falls back to the static icon, and the root is aria-hidden (pair with a label or ExperienceTypeBadge). Two tuned sizes: size="md" (32px) and size="sm" (16px).'
    >
      <VariantBlock label="Hover to animate · md" caption="32px — cover overlays">
        {TYPES.map((t) => (
          <Tile key={`md-${t.type}`} {...t} size="md" />
        ))}
      </VariantBlock>

      <VariantBlock label="Hover to animate · sm" caption="16px — rows & badges">
        {TYPES.map((t) => (
          <Tile key={`sm-${t.type}`} {...t} size="sm" />
        ))}
      </VariantBlock>

      <VariantBlock
        label="Always animating"
        caption="active=true — every signature at once, no hover needed"
      >
        {TYPES.map((t) => (
          <Tile key={`on-${t.type}`} {...t} size="md" forceActive />
        ))}
      </VariantBlock>

      <VariantBlock
        label="Sizes"
        caption='size="sm" (16px) vs size="md" (32px) — particle geometry is tuned to each'
      >
        <Tile type="PODCAST" label="sm · 16px" color="text-orange-500" size="sm" forceActive />
        <Tile type="PODCAST" label="md · 32px" color="text-orange-500" size="md" forceActive />
      </VariantBlock>

      <VariantBlock
        label="Aliasing + unknown type"
        caption="raw casing folds in; unknown types render the fallback"
      >
        <Tile type="Quiz" label={'"Quiz" → quest'} color="text-violet-500" size="md" forceActive />
        <Tile
          type="ai-note"
          label={'"ai-note" → note'}
          color="text-sky-500"
          size="md"
          forceActive
        />
        <Tile
          type="studio"
          label={'"studio" → fallback'}
          color="text-muted-foreground"
          size="md"
          forceActive
          fallback={<span className="text-[11px] text-muted-foreground">(fallback)</span>}
        />
      </VariantBlock>
    </Section>
  );
}
